import { useNostr } from '@nostrify/react';
import { useQuery, keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { MUSIC_TRACK_KIND, PLAYLIST_KIND } from '@/lib/musicTypes';
import type { NostrEvent } from '@nostrify/nostrify';
import { useEffect, useRef } from 'react';

export interface Notification {
  type: 'like' | 'comment' | 'zap';
  event: NostrEvent;
  targetEventId?: string;
  targetCoord?: string;
  targetEvent?: NostrEvent;
  isNew?: boolean; // Track if notification is new (unseen)
}

/**
 * Fetch all notification types for the current user's music tracks and playlists:
 * - Likes (kind 7 with ❤️ content) on tracks/playlists
 * - Comments (kind 1111) on tracks/playlists
 * - Zaps (kind 9735) on tracks/playlists
 */
// Helper to get seen notification IDs from localStorage
function getSeenNotificationIds(pubkey: string): Set<string> {
  const key = `seen_notifications_${pubkey}`;
  const stored = localStorage.getItem(key);
  return stored ? new Set(JSON.parse(stored)) : new Set();
}

// Helper to save seen notification IDs to localStorage
function saveSeenNotificationIds(pubkey: string, ids: Set<string>) {
  const key = `seen_notifications_${pubkey}`;
  localStorage.setItem(key, JSON.stringify([...ids]));
}

export function useNotifications(limit: number = 500) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();
  const seenIdsRef = useRef<Set<string>>(new Set());
  const hasInitializedRef = useRef(false);

  // Load seen notification IDs on mount (stable pubkey dependency)
  const pubkey = user?.pubkey;
  useEffect(() => {
    if (pubkey && !hasInitializedRef.current) {
      seenIdsRef.current = getSeenNotificationIds(pubkey);
      hasInitializedRef.current = true;
    }
  }, [pubkey]);

  const query = useQuery<Notification[]>({
    queryKey: ['notifications', user?.pubkey, limit],
    queryFn: async (c) => {
      if (!user) return [];

      // Get previously cached data to know which IDs we already have
      const previousData = queryClient.getQueryData<Notification[]>(['notifications', user?.pubkey, limit]);
      const previousIds = new Set(previousData?.map(n => n.event.id) || []);

      // Get seen notification IDs from localStorage
      const seenIds = getSeenNotificationIds(user.pubkey);

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(15000)]);

      try {
        // First, fetch all user's tracks and playlists
        const userContent = await nostr.query([
          {
            kinds: [MUSIC_TRACK_KIND, PLAYLIST_KIND],
            authors: [user.pubkey],
            limit: 1000, // Increased to ensure we get all user content
          },
        ], { signal });

        console.log(`[Notifications] Found ${userContent.length} tracks/playlists`);

        // Build list of target coordinates (kind:pubkey:d-tag)
        const targetCoords = userContent.map(event => {
          const d = event.tags.find(([name]) => name === 'd')?.[1] || '';
          return `${event.kind}:${event.pubkey}:${d}`;
        });

        if (targetCoords.length === 0) {
          // No tracks/playlists, no notifications
          console.log('[Notifications] No tracks/playlists found');
          return [];
        }

        console.log(`[Notifications] Querying for notifications on ${targetCoords.length} items with limit ${limit}`);

        // Query for all notification events that reference these tracks/playlists
        // Increased limit to fetch more historical notifications
        const filters = {
          kinds: [7, 1111, 9735], // likes, comments, zaps
          '#a': targetCoords, // Only events referencing our tracks/playlists
          limit, // Use the provided limit (default 500)
        };

        console.log(`[Notifications] Filter:`, JSON.stringify(filters, null, 2));

        const events = await nostr.query([filters], { signal });

        console.log(`[Notifications] Received ${events.length} notification events`);

        if (events.length === 0) {
          console.warn('[Notifications] No events received from relay. This might indicate a relay issue or rate limiting.');
        }

        // Sort by created_at (newest first)
        const sorted = events.sort((a, b) => b.created_at - a.created_at);

        // Process events into notifications
        const notificationMap = new Map<string, Notification>();
        const kindCounts = { likes: 0, comments: 0, zaps: 0 };

        for (const event of sorted) {
          let notification: Notification | null = null;

          if (event.kind === 7 && (event.content === '❤️' || event.content === '❤')) {
            // Like notification
            const eTag = event.tags.find(([name]) => name === 'e')?.[1];
            const aTag = event.tags.find(([name]) => name === 'a')?.[1];

            if (eTag || aTag) {
              // Find the target event
              const targetEvent = userContent.find(e => {
                if (aTag) {
                  const d = e.tags.find(([name]) => name === 'd')?.[1] || '';
                  return `${e.kind}:${e.pubkey}:${d}` === aTag;
                }
                return e.id === eTag;
              });

              // A notification is "new" if it's not in seen IDs AND not in previous cache
              // This means we only show the dot for truly new notifications, not ones we've already cached
              const isNew = !seenIds.has(event.id) && !previousIds.has(event.id);

              notification = {
                type: 'like',
                event,
                targetEventId: eTag,
                targetCoord: aTag,
                targetEvent,
                isNew,
              };
              kindCounts.likes++;
            }
          } else if (event.kind === 1111) {
            // Comment notification
            const eTag = event.tags.find(([name]) => name === 'e')?.[1];
            const aTag = event.tags.find(([name]) => name === 'a')?.[1];

            // Find the target event
            const targetEvent = userContent.find(e => {
              if (aTag) {
                const d = e.tags.find(([name]) => name === 'd')?.[1] || '';
                return `${e.kind}:${e.pubkey}:${d}` === aTag;
              }
              return e.id === eTag;
            });

            // A notification is "new" if it's not in seen IDs AND not in previous cache
            const isNew = !seenIds.has(event.id) && !previousIds.has(event.id);

            notification = {
              type: 'comment',
              event,
              targetEventId: eTag,
              targetCoord: aTag,
              targetEvent,
              isNew,
            };
            kindCounts.comments++;
          } else if (event.kind === 9735) {
            // Zap notification
            const eTag = event.tags.find(([name]) => name === 'e')?.[1];
            const aTag = event.tags.find(([name]) => name === 'a')?.[1];

            if (eTag || aTag) {
              // Find the target event
              const targetEvent = userContent.find(e => {
                if (aTag) {
                  const d = e.tags.find(([name]) => name === 'd')?.[1] || '';
                  return `${e.kind}:${e.pubkey}:${d}` === aTag;
                }
                return e.id === eTag;
              });

              // A notification is "new" if it's not in seen IDs AND not in previous cache
              const isNew = !seenIds.has(event.id) && !previousIds.has(event.id);

              notification = {
                type: 'zap',
                event,
                targetEventId: eTag,
                targetCoord: aTag,
                targetEvent,
                isNew,
              };
              kindCounts.zaps++;
            }
          }

          if (notification) {
            // Use event ID as key for deduplication
            notificationMap.set(event.id, notification);
          }
        }

        const finalNotifications = Array.from(notificationMap.values());
        console.log(`[Notifications] Processed: ${kindCounts.likes} likes, ${kindCounts.comments} comments, ${kindCounts.zaps} zaps (total: ${finalNotifications.length})`);

        // If we got 0 notifications but we had previous data, keep the previous data
        // This prevents relay rate limiting or connection issues from clearing the notifications
        if (finalNotifications.length === 0 && previousData && previousData.length > 0) {
          console.warn('[Notifications] Received 0 notifications but have cached data. Keeping cached data to prevent data loss.');
          return previousData;
        }

        return finalNotifications;
      } catch (err) {
        console.error('[Notifications] Failed to fetch notifications:', err);
        throw err;
      }
    },
    enabled: !!user,
    staleTime: Infinity, // Data never becomes stale automatically
    gcTime: Infinity, // Keep in cache forever (until manual invalidation)
    refetchInterval: false, // No automatic refetching
    retry: 2, // Retry failed queries up to 2 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnWindowFocus: false, // Don't refetch on window focus (prevents rate limiting)
    refetchOnReconnect: false, // Don't refetch on reconnect (prevents rate limiting)
    refetchOnMount: false, // Don't refetch when component remounts (use cached data)
    placeholderData: keepPreviousData, // Keep previous data while refetching
  });

  const notifications = query.data ?? [];

  // Ensure all arrays are properly typed and handle edge cases
  const commentNotifications = Array.isArray(notifications)
    ? notifications.filter(n => n && n.type === 'comment' && n.event)
    : [];
  const zapNotifications = Array.isArray(notifications)
    ? notifications.filter(n => n && n.type === 'zap' && n.event)
    : [];
  const likeNotifications = Array.isArray(notifications)
    ? notifications.filter(n => n && n.type === 'like' && n.event)
    : [];

  // Count new (unseen) notifications
  const unreadCount = Array.isArray(notifications)
    ? notifications.filter(n => n && n.isNew === true).length
    : 0;

  // Function to mark all current notifications as seen
  const markAllAsSeen = () => {
    if (!user || notifications.length === 0) return;

    // Add all current notification IDs to seen set
    const allIds = new Set([...seenIdsRef.current]);
    notifications.forEach(n => allIds.add(n.event.id));
    seenIdsRef.current = allIds;

    // Persist to localStorage
    saveSeenNotificationIds(user.pubkey, allIds);

    // Update cache to mark all as seen (isNew: false)
    queryClient.setQueryData<Notification[]>(
      ['notifications', user.pubkey, limit],
      notifications.map(n => ({ ...n, isNew: false }))
    );
  };

  return {
    notifications,
    commentNotifications,
    zapNotifications,
    likeNotifications,
    isLoading: query.isLoading,
    error: query.error,
    unreadCount, // Now counts only new notifications
    refetch: query.refetch, // Expose refetch for manual refresh
    markAllAsSeen, // Expose function to mark notifications as seen
  };
}
